/**
 * Places Premium LP — template preset.
 *
 * Um clique no admin (botão "Carregar template: Places Premium") popula
 * uma LP inteira com este preset. Todas as copies vieram do briefing final
 * do cliente (2026-02). Cliente edita imagens/logos/depoimentos diretamente
 * no CMS — copy pronta pra publicar em produção.
 *
 * IDs dos blocos são deterministicos (`lb_places_*`) pra facilitar
 * regeneração idempotente e reforçar que este é um preset controlado.
 */

const B = (id, type, extra) => ({ id: `lb_places_${id}`, type, spacing_px: type === "hero" ? 0 : 64, ...extra });

export const PLACES_PREMIUM_TEMPLATE = {
  slug: "places-premium",
  title: "Places Premium — LP alta performance B2B",
  description: "Landing page premium orientada à conversão B2B para incorporadoras. Template completo pronto pra editar e publicar.",
  published: false,
  noindex: false,
  meta_title: "Places Marketing Imobiliário | Branding, Lançamentos e Performance",
  meta_description: "Estratégia, branding, lançamento e performance para empreendimentos imobiliários. Conheça a Places e transforme projetos em marcas desejadas e oportunidades comerciais.",
  og_image: "https://agenciaplaces.com.br/og-default.jpg",
  primary_color: "#ff5100",
  sticky_cta: {
    enabled: true,
    label: "Quero falar sobre meu empreendimento",
    scroll_percent: 30,
    whatsapp_phone: "5562999999999",
    whatsapp_message: "Olá! Vim pela página da Places e quero conversar sobre um empreendimento.",
  },
  exit_intent: {
    enabled: true,
    delay_seconds: 45,
    scroll_percent: 40,
    title: "Antes de ir: qual é o desafio do seu empreendimento?",
    subtitle: "Uma conversa pode ajudar a enxergar o próximo movimento com mais clareza.",
    cta_label: "Quero conversar com a Places",
  },
  header: {
    enabled: true,
    brand_color: "white",
    link: "/",
  },
  blocks: [
    B("hero", "hero", {
      eyebrow: "BRANDING + MARKETING IMOBILIÁRIO + PERFORMANCE",
      title: "Seu empreendimento precisa ganhar o mercado antes de disputar a venda.",
      subtitle: "A Places conecta inteligência de produto, posicionamento, branding, campanha e performance para transformar empreendimentos imobiliários em marcas desejadas, gerar demanda qualificada e acelerar resultados comerciais.",
      cta_label: "Quero falar sobre meu empreendimento",
      cta_link: "#form-principal",
      cta_secondary_label: "Conhecer cases ↓",
      cta_secondary_link: "#cases",
      height: "90vh",
      overlay_opacity: 0.55,
      text_color: "white",
      show_brand: true,
      brand_color: "white",
      show_google_partner: true,
      google_partner_label: "Google Partner",
      image: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1920",
      microproof: "Quase 4 décadas de mercado imobiliário · R$ 15 bi+ em VGV lançado · 300+ empreendimentos · 17 estados · Google Partner",
    }),

    B("logos", "logos_band", {
      title: "Grandes projetos começam com grandes decisões.",
      subtitle: "Marcas e empreendimentos que já construíram histórias com a Places.",
      use_site_clients: true,
      client_names: [],
      columns: 6,
      logo_scale: 72,
      logos: [],
    }),

    B("problem", "features", {
      variant: "problem",
      title: "Um empreendimento não ganha mercado só porque é um bom produto.",
      subtitle: "Localização, arquitetura, lazer, preço e condição comercial importam. Mas, quando o mercado não percebe claramente o valor de um projeto, até bons produtos passam a competir apenas por atenção, mídia e preço. A Places atua antes, durante e depois do lançamento para transformar atributos imobiliários em percepção, desejo e oportunidade comercial.",
      columns: 4,
      items: [
        { icon: "package", title: "Produto", description: "O que faz esse empreendimento ser relevante?" },
        { icon: "compass", title: "Posicionamento", description: "Por que alguém deveria escolhê-lo?" },
        { icon: "message-circle", title: "Comunicação", description: "Como transformar diferenciais em desejo?" },
        { icon: "trending-up", title: "Performance", description: "Como colocar essa proposta diante das pessoas certas?" },
      ],
      footer: "É quando essas quatro respostas trabalham juntas que marketing deixa de ser apenas comunicação e passa a ajudar o empreendimento a ganhar mercado.",
      cta_label: "Quero construir essa estratégia",
      cta_link: "#form-principal",
    }),

    B("timeline", "process_timeline", {
      title: "Marketing imobiliário não começa no anúncio.",
      subtitle: "Começa quando o produto ainda está sendo pensado e continua até a última unidade encontrar seu comprador.",
      steps: [
        { label: "Inteligência de Produto", description: "Leitura de mercado, público, produto, argumentos, diferenciais e oportunidades." },
        { label: "Placemaking", description: "Transformação de espaços em lugares com significado, identidade e conexão." },
        { label: "Branding Imobiliário", description: "Naming, posicionamento, identidade visual, identidade verbal e construção de valor." },
        { label: "Estratégia de Lançamento", description: "Conceito, narrativa, campanha, canais, jornada e plano de comunicação." },
        { label: "Conteúdo", description: "Construção contínua de percepção, desejo, informação e autoridade." },
        { label: "Performance", description: "Meta, Google e estratégias digitais orientadas à geração de demanda." },
        { label: "Comercial", description: "Materiais de venda, campanhas para corretores e ações de relacionamento." },
        { label: "Sustentação", description: "Reposicionamento de argumentos, novas campanhas e ativações." },
      ],
      footer: "Uma estratégia. Múltiplos pontos de contato. Um objetivo: fazer o empreendimento avançar.",
    }),

    B("pillars", "features", {
      variant: "pillars",
      eyebrow: "WE ARE PLACERS",
      title: "Dados para entender.\nEstratégia para decidir.\nCriatividade para ser lembrado.",
      subtitle: "A Places une visão imobiliária, inteligência de mercado, branding, publicidade, tecnologia e performance em uma operação construída especificamente para empreendimentos imobiliários.",
      columns: 3,
      bg: "black",
      items: [
        { icon: "database", title: "DATA DRIVEN", description: "Dados ajudam a entender mercado, comportamento, mídia e oportunidades." },
        { icon: "layout", title: "DESIGN DRIVEN", description: "Estratégia ganha forma em marcas, campanhas e experiências capazes de aumentar percepção de valor." },
        { icon: "cpu", title: "IA DRIVEN", description: "Tecnologia e IA ampliam pesquisa, produção, análise, automação e velocidade sem substituir pensamento estratégico." },
      ],
      footer: "Tecnologia acelera. Dados orientam. Estratégia decide. Ideias diferenciam.",
    }),

    B("cases", "case_showcase", {
      eyebrow: "CASES PLACES",
      title: "Estratégia fica mais clara quando você vê o que ela é capaz de fazer.",
      cases: [
        { client: "Hard Rock · Gramado/RS", name: "Hard Rock Hotel Gramado", categories: "Branding · Lançamento · Campanha · Performance", challenge: "Marca global em um mercado local exigente pedia uma tradução premium sem perder a essência.", response: "A Places conectou branding local, campanha, mídia e ativações para gerar desejo e velocidade comercial.", image: "" },
        { client: "Áurea Incorporadora · Rio Verde/GO", name: "Stay. Smart Apart", categories: "Produto · Branding · Lançamento · Tráfego · Conteúdo", challenge: "Compacto precisava ser percebido não pela metragem, mas como solução clara de moradia e investimento.", response: "Conectamos produto, posicionamento, gestão de locação, branding, campanha e demanda pra facilitar a venda.", image: "" },
        { client: "Portal do Sol · Noronha/PE", name: "Portal do Sol Noronha", categories: "Placemaking · Branding · Campanha", challenge: "Empreendimento em ilha protegida exigia comunicação alinhada ao ecossistema e ao público.", response: "Construímos uma marca conceitual e uma campanha editorial que respeitou o território e mobilizou compradores certos.", image: "" },
        { client: "Wyndham · Sinop/MT", name: "Wyndham Oásis Resort Teles Pires", categories: "Branding · Lançamento · PDV · Mídia", challenge: "Resort no norte de MT precisava atrair investidores e turistas simultaneamente.", response: "Estratégia de multiproprieta­de com campanha nacional, PDV imersivo e mídia performance qualificaram a base de investidores.", image: "" },
      ],
      cta_label: "Ver case",
      cta_secondary_label: "Quero um projeto assim",
      cta_secondary_link: "#form-principal",
    }),

    B("legacy", "stats", {
      eyebrow: "THE REAL ESTATE LEGACY",
      title: "Experiência suficiente para conhecer os ciclos.\nInquietude suficiente para continuar mudando.",
      subtitle: "A Places acompanha as transformações do mercado imobiliário há quase quatro décadas. Produtos mudaram, cidades mudaram, a mídia mudou, o comportamento do comprador mudou, a tecnologia mudou. A obsessão continuou a mesma: entender o que faz um lugar conquistar pessoas.",
      columns: 4,
      bg: "neutral",
      items: [
        { value: "R$ 15", suffix: "bi+", label: "em VGV lançado" },
        { value: "17", suffix: "", label: "estados" },
        { value: "300", suffix: "+", label: "empreendimentos" },
        { value: "4", suffix: " décadas", label: "dedicadas ao mercado imobiliário" },
      ],
      footer: "Não chegamos ontem. Não iremos embora amanhã.",
    }),

    B("services", "services_grouped", {
      title: "Tudo o que seu empreendimento precisa para ganhar significado, mercado e velocidade.",
      groups: [
        { title: "Estratégia e Produto", items: ["Desenvolvimento de Produto", "Inteligência de Mercado", "Posicionamento", "Placemaking", "Estratégia Comercial"] },
        { title: "Branding", items: ["Naming", "Property Branding", "Identidade Visual", "Identidade Verbal", "Direção Fotográfica", "Brandbook"] },
        { title: "Lançamento e Publicidade", items: ["Conceito de Campanha", "Campanha de Lançamento", "Campanha de Sustentação", "Publicidade", "Mídia Offline", "PDV e Materiais Comerciais"] },
        { title: "Digital e Performance", items: ["Landing Pages", "Marketing Digital Imobiliário", "Meta Ads", "Google Ads", "Geração de Leads", "Conteúdo e Branded Content", "Automação e Tecnologia"] },
        { title: "Experiência e Vendas", items: ["Eventos", "Ativações", "Campanhas de Incentivo", "Comunicação para Corretores", "Ações Promocionais"] },
      ],
      footer: "A combinação muda de projeto para projeto. A visão integrada, não.",
      cta_label: "Falar sobre meu desafio",
      cta_link: "#form-principal",
    }),

    B("testimonials", "testimonial", {
      title: "Quem coloca milhões em um empreendimento não escolhe parceiros por acaso.",
      items: [
        { quote: "A Places entrega estratégia de verdade — não só campanha. Enxergaram nosso produto de um jeito que ninguém antes tinha enxergado.", author: "Nome do Diretor", role: "Diretor Comercial · Incorporadora Exemplo", avatar: "" },
        { quote: "Trabalhar com quem entende de VGV, corretor e velocidade de vendas muda o jogo. Recomendo a qualquer incorporadora séria.", author: "Nome do CEO", role: "CEO · Empresa Exemplo", avatar: "" },
        { quote: "O reposicionamento que a Places conduziu transformou um projeto que estava parado em um dos mais desejados da nossa cidade.", author: "Nome da Gestora", role: "Head de Marketing · Grupo Exemplo", avatar: "" },
      ],
    }),

    B("audiences", "features", {
      variant: "audiences",
      title: "Talvez você não precise de uma agência.\nTalvez precise de uma parceira de mercado.",
      subtitle: "A Places faz especialmente sentido para empresas que estão diante de decisões imobiliárias relevantes.",
      columns: 3,
      items: [
        { icon: "package-plus", title: "Novo empreendimento", description: "Estamos estruturando produto, conceito, posicionamento ou lançamento." },
        { icon: "sunrise", title: "Pré-lançamento", description: "Temos um projeto chegando ao mercado e precisamos construir desejo e diferenciação." },
        { icon: "rocket", title: "Lançamento", description: "Precisamos conectar campanha, mídia, geração de demanda e operação comercial." },
        { icon: "activity", title: "Sustentação", description: "O projeto já está no mercado e precisa recuperar atenção ou ganhar nova velocidade comercial." },
        { icon: "refresh-cw", title: "Reposicionamento", description: "O produto existe, mas sua comunicação já não traduz seu valor." },
        { icon: "building", title: "Marca institucional", description: "Precisamos fortalecer a incorporadora, loteadora ou grupo imobiliário." },
      ],
      cta_label: "Meu projeto está em uma dessas fases",
      cta_link: "#form-principal",
    }),

    B("process", "features", {
      variant: "steps",
      title: "Antes de apresentar uma solução, precisamos entender o problema certo.",
      columns: 4,
      items: [
        { icon: "1", title: "Conversa", description: "Entendemos empresa, empreendimento, mercado, momento e objetivos." },
        { icon: "2", title: "Diagnóstico", description: "Identificamos desafios, oportunidades e quais disciplinas precisam entrar." },
        { icon: "3", title: "Estratégia", description: "Desenhamos escopo, prioridades, canais e plano de atuação." },
        { icon: "4", title: "Execução", description: "Integramos criação, mídia, conteúdo, tecnologia, produção e acompanhamento." },
      ],
      footer: "Sem pacote pronto para problemas diferentes.",
      cta_label: "Começar uma conversa",
      cta_link: "#form-principal",
    }),

    B("faq", "faq", {
      title: "Antes de conversar, talvez você queira saber.",
      items: [
        { q: "A Places atende projetos fora de Goiás?", a: "Sim. A Places atua em diferentes mercados do Brasil e já participou de projetos em 17 estados." },
        { q: "Vocês trabalham somente com lançamentos?", a: "Não. A atuação pode começar ainda no desenvolvimento do produto e continuar em branding, lançamento, sustentação de vendas, reposicionamento, campanhas institucionais e ações comerciais." },
        { q: "É possível contratar somente branding?", a: "Sim. O escopo é construído de acordo com a necessidade estratégica de cada projeto." },
        { q: "A Places também trabalha com mídia digital e geração de leads?", a: "Sim. Estratégia digital, mídia de performance e geração de demanda fazem parte da operação e podem ser integradas às demais frentes do empreendimento." },
        { q: "Vocês trabalham com loteamentos e condomínios horizontais?", a: "Sim. A Places atua em diferentes verticais do mercado imobiliário, incluindo loteamentos, condomínios horizontais, verticais, multipropriedade, produtos de investimento e projetos especiais." },
        { q: "Já tenho uma equipe interna de marketing. A Places pode trabalhar junto?", a: "Sim. A atuação pode ser integrada ao departamento de marketing, equipe comercial, imobiliárias, parceiros, produtoras e demais fornecedores envolvidos." },
        { q: "Como solicitar uma proposta?", a: "O primeiro passo é uma conversa sobre o empreendimento. A partir do contexto, momento e objetivos, a equipe identifica o escopo mais adequado." },
      ],
      cta_label: "Quero conversar sobre meu projeto",
      cta_link: "#form-principal",
    }),

    B("form", "multi_step_form", {
      form_id: "form-principal",
      eyebrow: "PRONTO PARA COMEÇAR?",
      title: "Seu próximo empreendimento pode começar a ganhar mercado agora.",
      subtitle: "Conte para a Places o que você está construindo, lançando ou tentando acelerar. Vamos entender o cenário, o desafio e onde nossa experiência pode fazer diferença.",
      whatsapp_label: "Prefiro conversar pelo WhatsApp",
      whatsapp_phone: "5562999999999",
      whatsapp_message: "Olá! Vim pela página da Places e quero conversar sobre um empreendimento.",
      submit_label: "Quero conversar com a Places",
      microcopy: "Uma conversa inicial. Sem apresentação genérica. Sem compromisso.",
      lgpd_text: "Ao enviar meus dados, concordo em ser contatado pela Places sobre esta solicitação, conforme a Política de Privacidade.",
      thank_you_title: "Recebemos seu projeto. Obrigado pelo contato.",
      thank_you_body: "A equipe da Places vai analisar as informações e entrar em contato para entender melhor o seu momento. Enquanto isso, você também pode iniciar a conversa diretamente pelo WhatsApp.",
      steps: [
        {
          title: "Vamos começar pelo básico.",
          fields: [
            { name: "name", label: "Nome", type: "text", required: true },
            { name: "company", label: "Empresa", type: "text", required: true },
            { name: "phone", label: "WhatsApp", type: "tel", required: true },
            { name: "email", label: "E-mail corporativo", type: "email", required: true },
          ],
          next_label: "Continuar",
        },
        {
          title: "Conte um pouco sobre o momento do projeto.",
          fields: [
            { name: "stage", label: "Em que momento está o projeto?", type: "select", required: true,
              options: ["Em desenvolvimento", "Pré-lançamento", "Lançamento", "Sustentação de vendas", "Reposicionamento", "Estoque / produto pronto", "Institucional", "Ainda estamos estruturando"] },
            { name: "location", label: "Onde está localizado o empreendimento? (Cidade + UF)", type: "text", required: true },
            { name: "challenge", label: "Conte brevemente o principal desafio do projeto.", type: "textarea", required: false },
          ],
        },
      ],
    }),
  ],
};
